import { AnimatedContentLoader, Button, LucaIcon, ModalV2 } from '@components'
import { CompactPricingSuccessBadge } from '@components/CustomPricingTable/CompactPricingSuccessBadge'
import { BillingSetupPanel } from '@root/containers/Billing/BillingSetupPanel'
import {
  ContentCol,
  HeaderRow,
  IconCol,
  StyledCard
} from '@root/containers/Stripe/components/SubscriptionRowCard'
import {
  getPaymentMethodDetail,
  getPaymentMethodIcon,
  getPaymentMethodTitle
} from '@root/containers/Stripe/PaymentMethods/paymentMethodDisplay'
import { getSettingsPageUrl } from '@constants'
import { useBusinessContext } from '@context'
import {
  fetchLucaCreditBundleQuote,
  fetchLucaCreditBundles,
  ILucaCreditBundle,
  lucaUsageQueryKey,
  purchaseLucaCreditBundle
} from '@query'
import { usePaymentMethods, usePaymentMethodSetup } from '@root/query/hooks'
import { formatEurosWithOpts, formatNumber, getApiErrorMessage } from '@utils'
import { motion, Variants } from 'framer-motion'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaCheck } from 'react-icons/fa'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'

const REVIEW_BUNDLE_KEY = 'luca-credit-bundle-review'
const POPULAR_BUNDLE_ID = '5000'

type Step = 'bundles' | 'review' | 'payment'

// Fixed per-step sizes (like CustomPricingTableModal) so ModalV2 can tween width and height
// between steps. `autoHeight` resolves to max-content, which the browser cannot animate.
const MODAL_SIZE_BY_STEP: Record<Step, { width: number; height: number }> = {
  bundles: { width: 1000, height: 680 },
  review: { width: 600, height: 560 },
  payment: { width: 600, height: 900 }
}

// Same pop-in as PlanSelectionStep so the picker feels like the "Tilaa NoCFO" flow.
const cardsRowVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } }
}

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 260, damping: 22 } }
}

const packDescriptionKey = (bundleId: string): string => {
  if (bundleId === '1000' || bundleId === '5000' || bundleId === '10000') {
    return `settings.billing.lucaUsage.bundlePurchase.pack.${bundleId}`
  }
  return 'settings.billing.lucaUsage.bundlePurchase.pack.fallback'
}

const discountPercent = (bundle: ILucaCreditBundle): number => {
  if (bundle.list_amount <= 0) return 0
  const exact = (1 - bundle.amount / bundle.list_amount) * 100
  // 39 € against a 100 € list is 61%. Packs are advertised as 30 / 50 / 60.
  return Math.max(0, Math.floor(exact / 10) * 10)
}

const perHundredLabel = (bundle: ILucaCreditBundle): string => {
  if (bundle.credits <= 0) return formatEurosWithOpts(0)
  return formatEurosWithOpts((bundle.amount / bundle.credits) * 100)
}

interface Props {
  isVisible: boolean
  onClose: () => void
  balanceRemaining: number
}

export const LucaBundlePurchaseModal: React.FC<Props> = ({
  isVisible,
  onClose,
  balanceRemaining
}) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { slug, data: business } = useBusinessContext()
  const [requestedStep, setRequestedStep] = useState<Step>('bundles')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const purchaseKey = React.useRef<{ bundleId: string; key: string } | null>(null)
  const { startPaymentMethodSetup, isStartingPaymentMethodSetup } = usePaymentMethodSetup({
    slug
  })

  const bundlesQuery = useQuery({
    queryKey: [slug, 'luca-credit-bundles'],
    queryFn: () => fetchLucaCreditBundles({ slug }),
    enabled: isVisible && Boolean(slug)
  })

  const methodsQuery = usePaymentMethods({
    slug,
    enabled: isVisible && Boolean(business?.stripe_payg_checkout_enabled)
  })

  const bundles = useMemo(
    () => [...(bundlesQuery.data ?? [])].sort((a, b) => a.credits - b.credits),
    [bundlesQuery.data]
  )
  const middleBundleId = useMemo(() => {
    const popular = bundles.find(bundle => bundle.id === POPULAR_BUNDLE_ID)
    if (popular) return popular.id
    if (bundles.length === 0) return null
    return bundles[Math.floor((bundles.length - 1) / 2)].id
  }, [bundles])
  const highlightedId = selectedId ?? middleBundleId
  const selected = bundles.find(bundle => bundle.id === selectedId) ?? null
  const step: Step =
    requestedStep === 'review' && selectedId != null && selected == null && bundlesQuery.isFetched
      ? 'bundles'
      : requestedStep
  const savedCard =
    methodsQuery.data?.payment_methods.find(method => method.is_default) ??
    methodsQuery.data?.payment_methods[0]
  const hasCard = Boolean(savedCard?.last4)
  const SavedCardIcon = savedCard ? getPaymentMethodIcon(savedCard) : null

  // VAT comes from a Stripe tax preview so the buyer sees the charged total before "Buy".
  const quoteQuery = useQuery({
    queryKey: [slug, 'luca-credit-bundle-quote', selected?.id],
    queryFn: () => fetchLucaCreditBundleQuote({ slug, bundleId: selected?.id ?? '' }),
    enabled: isVisible && step === 'review' && selected != null
  })
  const quote = quoteQuery.data?.id === selected?.id ? quoteQuery.data : undefined
  const vatQuote =
    quote && quote.tax_amount != null && quote.total_amount != null
      ? { ...quote, tax_amount: quote.tax_amount, total_amount: quote.total_amount }
      : null
  const isQuoteLoading = quoteQuery.isLoading && !quote

  // The review step shows the current balance, so refresh it on every entry.
  const showReview = useCallback(() => {
    setRequestedStep('review')
    void queryClient.invalidateQueries({ queryKey: lucaUsageQueryKey(slug) })
  }, [queryClient, slug])

  useEffect(() => {
    if (!isVisible) {
      setRequestedStep('bundles')
      setSelectedId(null)
      return
    }
    const storedId = sessionStorage.getItem(REVIEW_BUNDLE_KEY)
    if (!storedId) {
      setRequestedStep('bundles')
      setSelectedId(null)
      return
    }
    sessionStorage.removeItem(REVIEW_BUNDLE_KEY)
    setSelectedId(storedId)
    setRequestedStep('review')
  }, [isVisible])

  const idempotencyKeyFor = (bundleId: string) => {
    if (purchaseKey.current?.bundleId !== bundleId) {
      purchaseKey.current = { bundleId, key: crypto.randomUUID() }
    }
    return purchaseKey.current.key
  }

  const purchaseMutation = useMutation({
    mutationFn: (bundleId: string) =>
      purchaseLucaCreditBundle(
        { slug },
        { bundle_id: bundleId, idempotency_key: idempotencyKeyFor(bundleId) }
      ),
    onSuccess: async result => {
      if (result.checkout_url) {
        window.location.href = result.checkout_url
        return
      }
      if (!result.completed || !selected) return
      purchaseKey.current = null
      await queryClient.invalidateQueries({ queryKey: lucaUsageQueryKey(slug) })
      await queryClient.invalidateQueries({
        predicate: query => query.queryKey.includes('widget-session')
      })
      toast.success(
        t('settings.billing.lucaUsage.bundlePurchase.success', {
          credits: formatNumber(selected.credits)
        })
      )
      onClose()
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, t('settings.billing.lucaUsage.bundlePurchase.error')))
    }
  })

  const changeCard = async () => {
    if (!selected) return
    sessionStorage.setItem(REVIEW_BUNDLE_KEY, selected.id)
    try {
      await startPaymentMethodSetup({
        returnPath: getSettingsPageUrl(slug, { page: 'luca-usage', panel: 'bundles' })
      })
    } catch (error) {
      sessionStorage.removeItem(REVIEW_BUNDLE_KEY)
      toast.error(getApiErrorMessage(error, t('settings.billing.lucaUsage.bundlePurchase.error')))
    }
  }

  const paygBundle = bundles.find(bundle => bundle.credits > 0)
  const paygPerHundred =
    paygBundle != null
      ? formatEurosWithOpts((paygBundle.list_amount / paygBundle.credits) * 100, {
          avoidDecimals: true
        })
      : null

  const footer = (
    <FooterActions>
      {step === 'payment' ? (
        <Button isSecondary onClick={showReview}>
          {t('general.back')}
        </Button>
      ) : (
        <Button onClick={onClose} disabled={purchaseMutation.isPending}>
          {t('general.close')}
        </Button>
      )}
    </FooterActions>
  )

  return (
    <ModalV2
      isVisible={isVisible}
      handleOnClose={onClose}
      header={t('settings.billing.lucaUsage.bundlePurchase.title')}
      headerIcon={<LucaIcon size="1.1em" />}
      width={MODAL_SIZE_BY_STEP[step].width}
      height={MODAL_SIZE_BY_STEP[step].height}
      footer={footer}
    >
      {step === 'bundles' && (
        <BundlesStep>
          <Intro>
            {t('settings.billing.lucaUsage.bundlePurchase.intro')}
            <br />
            {t('settings.billing.lucaUsage.bundlePurchase.introDetail')}
          </Intro>
          <AnimatedContentLoader
            isLoading={bundlesQuery.isLoading}
            isError={bundlesQuery.isError}
            errorDescription={
              <Intro>{t('settings.billing.lucaUsage.bundlePurchase.loadError')}</Intro>
            }
          >
            <CardsRow variants={cardsRowVariants} initial="hidden" animate="show">
              {bundles.map(bundle => {
                const discount = discountPercent(bundle)
                const isPopular = bundle.id === POPULAR_BUNDLE_ID
                const select = () => {
                  setSelectedId(bundle.id)
                  showReview()
                }
                return (
                  <BundleCard
                    key={bundle.id}
                    variants={cardVariants}
                    $selected={bundle.id === highlightedId}
                  >
                    <CardTop>
                      <NameRow>
                        <Credits>{formatNumber(bundle.credits)}</Credits>
                        <CreditsUnit>
                          {t('settings.billing.lucaUsage.bundlePurchase.creditsUnit')}
                        </CreditsUnit>
                        {discount > 0 && (
                          <CompactPricingSuccessBadge>
                            {t('settings.billing.lucaUsage.bundlePurchase.discount', {
                              percent: discount
                            })}
                          </CompactPricingSuccessBadge>
                        )}
                      </NameRow>
                      <PackCopy>{t(packDescriptionKey(bundle.id))}</PackCopy>
                      <Spacer />
                      <PriceRow>
                        <PriceNumber>{Math.round(bundle.amount / 100)}</PriceNumber>
                        <PriceSuffix>€</PriceSuffix>
                        <PriceUnit>
                          {t('settings.billing.lucaUsage.bundlePurchase.oneTime')}
                        </PriceUnit>
                      </PriceRow>
                    </CardTop>
                    <FullWidthButton
                      type="button"
                      intent={isPopular ? 'primary' : undefined}
                      onClick={select}
                    >
                      {t('settings.billing.lucaUsage.bundlePurchase.select')}
                    </FullWidthButton>
                    <FeatureList>
                      <FeatureItem>
                        <FaCheck />
                        {t('settings.billing.lucaUsage.bundlePurchase.perHundred', {
                          price: perHundredLabel(bundle)
                        })}
                      </FeatureItem>
                      <FeatureDivider />
                      <FeatureItem>
                        <FaCheck />
                        {t('settings.billing.lucaUsage.bundlePurchase.noExpiry')}
                      </FeatureItem>
                    </FeatureList>
                  </BundleCard>
                )
              })}
            </CardsRow>
            {paygPerHundred != null && (
              <PaygNote>
                {t('settings.billing.lucaUsage.bundlePurchase.paygUnitPrice', {
                  price: paygPerHundred
                })}
              </PaygNote>
            )}
          </AnimatedContentLoader>
        </BundlesStep>
      )}

      {step === 'review' && (
        <ReviewWrapper>
          <AnimatedContentLoader isLoading={!selected || isQuoteLoading}>
            {selected ? (
              <ReviewPanel>
                <ReviewTitle>
                  {t('settings.billing.lucaUsage.bundlePurchase.summaryTitle')}
                </ReviewTitle>
                <SummaryList>
                  <ReviewLine>
                    <span>
                      {t('settings.billing.lucaUsage.bundlePurchase.packLine', {
                        credits: formatNumber(selected.credits)
                      })}
                    </span>
                    <span>{formatEurosWithOpts(quote?.amount ?? selected.amount)}</span>
                  </ReviewLine>
                  <ReviewLine>
                    <span>
                      {vatQuote?.tax_rate_percent != null
                        ? t('settings.billing.lucaUsage.bundlePurchase.vatLabelWithRate', {
                            rate: formatNumber(vatQuote.tax_rate_percent)
                          })
                        : t('settings.billing.lucaUsage.bundlePurchase.vatLabel')}
                    </span>
                    <span>
                      {vatQuote
                        ? formatEurosWithOpts(vatQuote.tax_amount)
                        : t('settings.billing.lucaUsage.bundlePurchase.vatValue')}
                    </span>
                  </ReviewLine>
                  <TotalLine>
                    <span>
                      {vatQuote
                        ? t('settings.billing.lucaUsage.bundlePurchase.total')
                        : t('settings.billing.lucaUsage.bundlePurchase.totalExclVat')}
                    </span>
                    <span>
                      {vatQuote
                        ? formatEurosWithOpts(vatQuote.total_amount)
                        : formatEurosWithOpts(quote?.amount ?? selected.amount)}
                    </span>
                  </TotalLine>
                </SummaryList>
                <ReviewLine>
                  <span>{t('settings.billing.lucaUsage.bundlePurchase.balance')}</span>
                  <Mono>
                    {formatNumber(balanceRemaining)} →{' '}
                    {formatNumber(balanceRemaining + selected.credits)}
                  </Mono>
                </ReviewLine>
                {hasCard && savedCard && SavedCardIcon && (
                  <StyledCard $isActive={false}>
                    <IconCol $isActive={false}>
                      <SavedCardIcon aria-hidden />
                    </IconCol>
                    <ContentCol>
                      <HeaderRow>
                        <h4>{getPaymentMethodTitle(savedCard, t)}</h4>
                        <LinkButton type="button" onClick={() => setRequestedStep('payment')}>
                          {t('settings.billing.lucaUsage.bundlePurchase.changeCard')}
                        </LinkButton>
                      </HeaderRow>
                      {getPaymentMethodDetail(savedCard, t) && (
                        <CardMeta>{getPaymentMethodDetail(savedCard, t)}</CardMeta>
                      )}
                    </ContentCol>
                  </StyledCard>
                )}
                <ActionRow>
                  <Button
                    isSecondary
                    onClick={() => setRequestedStep('bundles')}
                    disabled={purchaseMutation.isPending}
                  >
                    {t('settings.billing.lucaUsage.bundlePurchase.backToBundles')}
                  </Button>
                  <Button
                    intent={hasCard ? 'success' : 'primary'}
                    onClick={() => purchaseMutation.mutate(selected.id)}
                    disabled={purchaseMutation.isPending}
                    showSpinner={purchaseMutation.isPending}
                  >
                    {hasCard
                      ? t('settings.billing.lucaUsage.bundlePurchase.buy')
                      : t('settings.billing.lucaUsage.bundlePurchase.continueToPayment')}
                  </Button>
                </ActionRow>
              </ReviewPanel>
            ) : null}
          </AnimatedContentLoader>
        </ReviewWrapper>
      )}

      {step === 'payment' && (
        <BillingSetupPanel
          mode="payment-method"
          initialSelectedCardId="card"
          onStripeAction={() => {
            void changeCard()
          }}
          isStripeActionPending={isStartingPaymentMethodSetup}
        />
      )}
    </ModalV2>
  )
}

const BundlesStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xl}rem;
  padding: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.md}rem 0;
`

const Intro = styled.p`
  margin: 0;
  max-width: 36rem;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.metalGray};
`

const CardsRow = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md}rem;
  width: 100%;
`

/* Mirrors PricingCard so the bundle picker reads like the "Tilaa NoCFO" flow. */
const BundleCard = styled(motion.div)<{ $selected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
  flex: 1 1 260px;
  min-width: 0;
  min-height: 340px;
  max-width: 420px;
  padding: ${({ theme }) => theme.spacing.xl}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  border: 2px solid
    ${({ theme, $selected }) => ($selected ? theme.colors.primary : theme.colors.neutralGray)};
  background: ${({ theme }) => theme.colors.neutralWhite};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const CardTop = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const NameRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const Credits = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xl}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
  white-space: nowrap;
`

const CreditsUnit = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`

const PackCopy = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.metalGray};
`

const Spacer = styled.div`
  flex: 1;
`

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.1rem;
  margin-top: ${({ theme }) => theme.spacing.sm}rem;
`

const PriceNumber = styled.span`
  font-size: 2.6rem;
  font-weight: 600;
  line-height: 1;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const PriceSuffix = styled.span`
  font-size: ${({ theme }) => theme.fontSize.lg}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
  align-self: flex-start;
  margin-top: 0.3rem;
`

const PriceUnit = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  margin-left: ${({ theme }) => theme.spacing.xxs}rem;
`

const FullWidthButton = styled(Button)`
  width: 100%;
  justify-content: center;
  border-radius: 999px;
`

const FeatureList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  min-height: 80px;
`

const FeatureDivider = styled.li`
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};

  svg {
    flex-shrink: 0;
    fill: ${({ theme }) => theme.colors.primary};
  }
`

const ReviewWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md}rem;
`

const ReviewPanel = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 36rem;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const ReviewTitle = styled.h4`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const SummaryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const ReviewLine = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`

const TotalLine = styled(ReviewLine)`
  padding-top: ${({ theme }) => theme.spacing.sm}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-weight: 600;
`

const Mono = styled.span`
  font-family: 'Roboto Mono', ui-monospace, monospace;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const CardMeta = styled.span`
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
`

const LinkButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  width: 100%;
`

const PaygNote = styled.p`
  margin: ${({ theme }) => theme.spacing.md}rem 0 0;
  width: 100%;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.metalGray};
`
